import { ORGANIZATION, SITE_URL } from './constants';

export function organizationSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: ORGANIZATION.name,
        alternateName: ORGANIZATION.alternateName,
        url: ORGANIZATION.url,
        email: ORGANIZATION.email,
        telephone: ORGANIZATION.telephone,
    };
}

export function webSiteSchema() {
    return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: ORGANIZATION.name,
        alternateName: ORGANIZATION.alternateName,
        url: SITE_URL,
        inLanguage: 'ar-EG',
        publisher: {
            '@type': 'Organization',
            name: ORGANIZATION.name,
            url: ORGANIZATION.url,
        },
    };
}

export function courseSchema({ name, description, url, image }) {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name,
        description,
        url,
        provider: {
            '@type': 'Organization',
            name: ORGANIZATION.name,
            url: ORGANIZATION.url,
        },
    };
    if (image) {
        schema.image = image;
    }
    return schema;
}

export function faqPageSchema(items) {
    if (!items || !items.length) {
        return null;
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
                '@type': 'Answer',
                text: item.a,
            },
        })),
    };
}

export function webPageSchema({
    name,
    description,
    url,
    inLanguage = 'ar-EG',
    about = [],
    datePublished,
    dateModified,
}) {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name,
        description,
        url,
        inLanguage,
        isPartOf: {
            '@type': 'WebSite',
            name: ORGANIZATION.name,
            url: SITE_URL,
        },
    };

    if (about && about.length) {
        schema.about = about.map((topic) => ({
            '@type': 'Thing',
            name: topic,
        }));
    }

    if (datePublished) {
        schema.datePublished = datePublished;
    }
    if (dateModified) {
        schema.dateModified = dateModified;
    }

    return schema;
}

export function breadcrumbListSchema(items) {
    if (!items || !items.length) {
        return null;
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: absoluteUrl(item.url),
        })),
    };
}

export function personSchema({ name, alternateName, url }) {
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name,
        url: absoluteUrl(url),
    };
    if (alternateName) {
        schema.alternateName = alternateName;
    }
    return schema;
}

export function itemListSchema({ name, items }) {
    if (!items || !items.length) {
        return null;
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name,
        numberOfItems: items.length,
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            url: item.url ? absoluteUrl(item.url) : undefined,
            description: item.description || undefined,
        })),
    };
}

export function bookSchema({
    name,
    url,
    inLanguage,
    educationalLevel,
    description,
}) {
    return {
        '@context': 'https://schema.org',
        '@type': 'Book',
        name,
        url: absoluteUrl(url),
        inLanguage,
        bookFormat: 'EBook',
        encodingFormat: 'application/pdf',
        educationalLevel,
        description,
        publisher: {
            '@type': 'Organization',
            name: ORGANIZATION.name,
            url: ORGANIZATION.url,
        },
    };
}

export function imageObjectSchema({
    name,
    description,
    url,
    contentUrl,
    width,
    height,
    inLanguage = 'ar',
    caption,
    pageUrl,
    pageName,
}) {
    const resolvedUrl = absoluteUrl(contentUrl || url);
    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        name,
        description,
        contentUrl: resolvedUrl,
        url: resolvedUrl,
        encodingFormat: 'image/png',
        inLanguage,
        creator: {
            '@type': 'Organization',
            name: ORGANIZATION.name,
            url: ORGANIZATION.url,
        },
        copyrightHolder: {
            '@type': 'Organization',
            name: ORGANIZATION.name,
            url: ORGANIZATION.url,
        },
    };
    if (width) schema.width = width;
    if (height) schema.height = height;
    if (caption) schema.caption = caption;
    if (pageUrl) {
        schema.isPartOf = {
            '@type': 'WebPage',
            '@id': absoluteUrl(pageUrl),
            name: pageName || undefined,
        };
    }
    return schema;
}

export function buildJsonLdGraph(schemas) {
    const list = schemas.filter(Boolean);
    if (!list.length) {
        return null;
    }
    if (list.length === 1) {
        return list[0];
    }
    return {
        '@context': 'https://schema.org',
        '@graph': list,
    };
}

export function absoluteUrl(path) {
    if (!path) {
        return SITE_URL;
    }
    if (/^https?:\/\//i.test(path)) {
        return path;
    }
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${SITE_URL}${normalized}`;
}
